import { HttpClient } from '@wix/http-client';
import { ErrorReporter } from '@wix/editor-error-reporter';
import {
  AI_IMAGE_GENERATION_SERVERLESS_BASE_URL,
  FLUX_1_DEV_IMAGE_GENERATION_PROMPT_ID,
  FLUX_1_DEV_BLACKLIST_IMAGE_GENERATION_PROMPT_ID,
} from './consts';
import { GetImageParams, PromptsData } from './types';
import { getImagesTheme } from '@wix/ambassador-genie-v1-compose/http';
import _ from 'lodash';

interface PromptsRequestOptions {
  scenePromptVer: string;
  structurePromptVer: string;
  imageDescriptions: Record<string, object>;
  siteDescription?: string;
  businessTerm: string;
}

export const getPromptsKeysFromServerless = async (
  requestOptions: PromptsRequestOptions,
  msId: string,
  msInstance: string,
): Promise<{ results: PromptsData } | undefined> => {
  const httpClient = new HttpClient({
    headers: { Authorization: msInstance },
  });

  const url = `${AI_IMAGE_GENERATION_SERVERLESS_BASE_URL}prompts/?metaSiteId=${msId}`;

  return httpClient
    .post(url, requestOptions)
    .then(({ data }) => data)
    .catch((e) => {
      ErrorReporter.captureException(e, {
        tags: { aiImageGeneratorRequest: true },
        extra: { url, requestOptions },
      });
    });
};

const handleRequest = async (
  msInstance: string,
  url: string,
  requestOptions: object,
): Promise<{ url: string } | undefined> => {
  const httpClient = new HttpClient({
    headers: { Authorization: msInstance },
  });

  return httpClient
    .post(url, requestOptions)
    .then(({ data }) => data)
    .catch((e) => {
      ErrorReporter.captureException(e, {
        tags: {
          aiImageGeneratorRequest: true,
          aiImageGenerationServerless: true,
        },
        extra: { url, requestOptions },
      });
    });
};

export const getImageFromServerlessBase = async (params: GetImageParams) => {
  const url = `${AI_IMAGE_GENERATION_SERVERLESS_BASE_URL}image-by-prompt-object`;
  const requestOptions = {
    promptKey: params.promptKey,
    imageGenerationPromptId: FLUX_1_DEV_IMAGE_GENERATION_PROMPT_ID,
    compId: params.compId,
    raw: true,
    ...params.imageResolution,
  };
  return handleRequest(params.msInstance, url, requestOptions);
};

export const getImageFromServerlessBlacklisted = async (
  params: GetImageParams,
) => {
  const url = `${AI_IMAGE_GENERATION_SERVERLESS_BASE_URL}image-by-prompt-object-blacklisted`;
  const requestOptions = {
    promptKey: params.promptKey,
    imageGenerationPromptId: FLUX_1_DEV_BLACKLIST_IMAGE_GENERATION_PROMPT_ID,
    compId: params.compId,
    raw: true,
    aspectRatio: params.aspectRatio,
  };
  return handleRequest(params.msInstance, url, requestOptions);
};

export const getImageFromServerless = async (params: GetImageParams) => {
  return params.isAiImageNSFWBlacklisted
    ? getImageFromServerlessBlacklisted(params)
    : getImageFromServerlessBase(params);
};

export const getPhotoTheme = async (
  httpClient: HttpClient,
): Promise<string> => {
  const defaultPhotoThemeValue =
    'Natural colors, soft light, minimal atmosphere, Professional camera';

  try {
    const response = await httpClient.request(getImagesTheme({}));
    const theme = response.data.imagesTheme?.value;

    return theme ?? defaultPhotoThemeValue;
  } catch (error: any) {
    const errorResponse = error.response?.data;
    console.error(
      'Failed to fetch photo theme. Using default value. Error',
      errorResponse,
    );

    return defaultPhotoThemeValue;
  }
};

export const getBlob = async (url: string): Promise<Blob | undefined> => {
  const httpClient = new HttpClient();

  return httpClient
    .get(url, { responseType: 'blob' })
    .then(({ data }) => data)
    .catch((e) => {
      ErrorReporter.captureException(e, {
        tags: {
          aiImageGeneratorRequest: true,
        },
        extra: { url },
      });
    });
};
